/**
 * Pure pagination derivation shared by TableExplorerTab and
 * TableExplorerPagination.
 *
 * Handles three regimes:
 *  - **Known total** (the normal SQL case): a `COUNT(*)` gives an exact total,
 *    so the page count and Next/Prev availability are exact.
 *  - **Estimated total**: the exact `COUNT(*)` missed its settle budget on an
 *    UNFILTERED fetch, and the backend returned the engine's own catalog
 *    row-count statistic instead (`estimatedRowCount` — see
 *    `TableExplorerFetchRowsResult`). The pager works off that number so the
 *    user still gets a page count and numbered navigation immediately, but the
 *    statistic can be stale in either direction, so the **has-more guard stays
 *    on**: a full last page still offers Next, and the page count never falls
 *    below the page the user is actually on.
 *  - **Unknown total** (`totalRowCount` and `estimatedRowCount` both absent):
 *    the count was skipped or abandoned and no estimate was available — Kusto
 *    skips it outright (`Table | count` over the App Insights / Log Analytics
 *    ADX proxy scans the entire retention window), and SQLite/mock have no
 *    catalog statistic to fall back to. Without any total we cannot know the
 *    page count, so "is there a next page?" is inferred purely from whether the
 *    current page came back full.
 */
/**
 * Tooltip shown on any total rendered with the "≈" prefix. Lives here so the
 * pagination strip and the status bar — the two surfaces that can display the
 * row count — always explain it the same way.
 */
export const ESTIMATED_TOTAL_TITLE =
  'Approximate total from the table statistics — the exact row count took too long to return.';

export type PaginationModel = {
  /** Page count. Always ≥ 1. Meaningless-but-safe (1) when the total is unknown. */
  totalPages: number;
  /** True when no total at all is available — neither exact nor estimated (see module doc). */
  totalUnknown: boolean;
  /**
   * True when `effectiveTotal` came from catalog statistics rather than a
   * `COUNT(*)`. The UI renders such a total with a "≈" prefix.
   */
  totalIsEstimate: boolean;
  /**
   * The total the pager is working from: the exact count, else the estimate,
   * else 0. Callers display this (with "≈" when `totalIsEstimate`).
   */
  effectiveTotal: number;
  /** Whether a Next page should be offered. */
  hasNextPage: boolean;
  /** Whether a Previous page should be offered. */
  hasPrevPage: boolean;
  /** 1-based index of the first row shown (0 when nothing is loaded). */
  rangeStart: number;
  /** 1-based index of the last row shown (0 when nothing is loaded). */
  rangeEnd: number;
  rangeMode: 'global' | 'loaded';
};

export function derivePaginationModel(input: {
  currentPage: number;
  pageSize: number;
  totalRowCount: number | undefined;
  /**
   * Approximate whole-table total from catalog statistics. Used ONLY when
   * `totalRowCount` is undefined; an exact count always wins.
   */
  estimatedRowCount?: number | undefined;
  /** Rows actually returned for the current page — drives has-more when the total is unknown. */
  loadedRowCount: number;
  /** Explicit continuation truth for cursor-backed engines. */
  hasMoreOverride?: boolean;
  /** Loaded-page mode never implies a global offset or total. */
  rangeMode?: 'global' | 'loaded';
}): PaginationModel {
  const { currentPage, pageSize, totalRowCount, estimatedRowCount, loadedRowCount, hasMoreOverride, rangeMode = 'global' } = input;

  // A non-positive estimate is no estimate: every engine uses one as its
  // "never analyzed" sentinel, and the backend already filters those out.
  const totalIsEstimate =
    totalRowCount === undefined && typeof estimatedRowCount === 'number' && estimatedRowCount > 0;
  const totalUnknown = totalRowCount === undefined && !totalIsEstimate;
  const effectiveTotal = totalRowCount ?? (totalIsEstimate ? (estimatedRowCount as number) : 0);

  // A full page implies there may be another; a short/empty page is the end.
  const pageLooksFull = loadedRowCount >= pageSize && loadedRowCount > 0;

  // With an estimate the page count is a guess, so it must never contradict
  // where the user already is: an under-estimate would otherwise collapse the
  // pager to "1" while the user reads page 4.
  const estimatedPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const totalPages = totalIsEstimate ? Math.max(estimatedPages, currentPage) : estimatedPages;

  const hasPrevPage = currentPage > 1;
  const derivedHasNextPage = totalUnknown
    ? pageLooksFull
    : totalIsEstimate
      ? // Estimated: trust the page count, but keep the has-more guard so an
        // under-estimate can still be paged past.
        currentPage < totalPages || pageLooksFull
      : currentPage < totalPages;

  const hasNextPage = hasMoreOverride ?? derivedHasNextPage;
  const rangeStart = loadedRowCount > 0 ? (rangeMode === 'loaded' ? 1 : (currentPage - 1) * pageSize + 1) : 0;
  // Only an exact total may clamp the range end. With no total, or an estimated
  // one, the honest end is what actually came back for this page.
  const rangeEnd = rangeMode === 'loaded'
    ? loadedRowCount
    : totalRowCount === undefined
    ? (currentPage - 1) * pageSize + loadedRowCount
    : Math.min(currentPage * pageSize, effectiveTotal);

  return {
    totalPages,
    totalUnknown,
    totalIsEstimate,
    effectiveTotal,
    hasNextPage,
    hasPrevPage,
    rangeStart,
    rangeEnd,
    rangeMode,
  };
}
